import useSWR from 'swr';
import { conversationService, isApiError } from '@dopomogai/api-client';
import type { ConversationSummaryDetail, UUID } from '@dopomogai/types';
import { AgentKitError } from '../types';

type SummaryMap = Record<UUID, ConversationSummaryDetail | null>;

const summariesFetcher = async ([, ids]: [string, UUID[]]): Promise<SummaryMap> => {
  const summaries: SummaryMap = {};

  for (const id of ids) {
    const response = await conversationService.getConversationSummary(id);
    if (isApiError(response)) {
      if (response.status === 404) {
        summaries[id] = null;
        continue;
      }
      throw new AgentKitError(
        response.message || `Failed to fetch summary for conversation ${id}`,
        response,
      );
    }
    summaries[id] = response;
  }

  return summaries;
};

export const useConversationSummaries = (conversationIds: UUID[]) => {
  const sortedIds = [...conversationIds].sort();
  const cacheKey =
    sortedIds.length > 0 ? ['conversation-summaries', sortedIds] : null;

  const { data, error, isLoading } = useSWR<SummaryMap, AgentKitError>(
    cacheKey,
    summariesFetcher,
    {
      keepPreviousData: true,
    },
  );

  return {
    summariesById: data ?? {},
    isLoading,
    error,
  };
};
